import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, ChevronLeft, Inbox, Mail, MailOpen, Paperclip, PenSquare, RefreshCw, Sparkles, Star, Tag, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/ui/EmptyState';
import { MAX_REPLY_ATTACHMENT_BYTES, MAX_REPLY_ATTACHMENT_MB } from '../../constants/upload';
import { mailboxColor as hueFor } from '../mailboxColors';
import { outreachApi } from '../api/outreachApi';
import { clearReplyDraft, loadReplyDraft, saveReplyDraft } from '../replyDrafts';
import UniboxLeadStatus from '../components/UniboxLeadStatus';
import { useUnread } from '../UnreadContext';
import '../outreach.css';

const PAGE_SIZE = 50;

const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// "Inbox" is everything that isn't warm-up, which is what the tab used to
// say "Replied" for - human_reply is the catch-all bucket, so a Zoho login
// notice landed under a label promising answers.
const TABS = [
  { key: 'inbox', labelKey: 'outreach.tabs.inbox' },
  {
    key: 'waiting',
    waitingOnly: true,
    labelKey: 'outreach.tabs.waiting',
    count: 'overdue',
    countTitleKey: 'outreach.overdueCount',
  },
  { key: 'campaignReplies', campaignRepliesOnly: true, labelKey: 'outreach.tabs.campaignReplies' },
  { key: 'unread', unreadOnly: true, labelKey: 'outreach.tabs.unread', count: 'unread', countTitleKey: 'outreach.unreadCount' },
  { key: 'starred', starredOnly: true, labelKey: 'outreach.tabs.starred' },
];

const tabFilters = (key) => {
  const tab = TABS.find((item) => item.key === key);
  return {
    unreadOnly: tab.unreadOnly,
    starredOnly: tab.starredOnly,
    waitingOnly: tab.waitingOnly,
    campaignRepliesOnly: tab.campaignRepliesOnly,
  };
};

const SEARCH_DEBOUNCE_MS = 350;
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

const BADGE_CLASS = {
  bounce_hard: 'is-bounce',
  bounce_soft: 'is-bounce',
  ooo: 'is-ooo',
  auto_reply: 'is-auto',
};

function formatDate(value, language) {
  if (!value) return '';
  const date = new Date(value);
  const elapsed = Date.now() - date.getTime();
  const relative = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  if (elapsed < MINUTE) return relative.format(0, 'minute');
  if (elapsed < HOUR) return relative.format(-Math.floor(elapsed / MINUTE), 'minute');
  if (elapsed < DAY) return relative.format(-Math.floor(elapsed / HOUR), 'hour');
  if (elapsed < WEEK) return relative.format(-Math.floor(elapsed / DAY), 'day');
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(language, {
    day: 'numeric',
    month: 'short',
    year: sameYear ? undefined : 'numeric',
  });
}

const waitingDays = (since) => Math.floor((Date.now() - new Date(since).getTime()) / DAY);

// Stable per mailbox so the same account keeps its colour across renders.
// A bare IMAP uid is unique only within one mailbox; the default view
// merges every connected account, so anything keyed by uid alone can
// show one account's body under another account's message.
const messageKey = (message) => `${message.account_id}:${message.folder}:${message.uid}`;

// Same reason: a flag change has to go to the mailbox each uid belongs to.
// Our own copies live in Sent, where the same uid is a different message.
const byAccount = (messages) =>
  messages
    .filter((message) => !message.is_outbound)
    .reduce((grouped, message) => {
      (grouped[message.account_id] ||= []).push(message.uid);
      return grouped;
    }, {});

export default function UniboxPage() {
  const { t, i18n } = useTranslation('common');
  const { token } = useAuth();

  const [accounts, setAccounts] = useState([]);
  // Tab, mailbox and query live in the URL so a reload, a back button or a
  // pasted link all land on the same view.
  const [params, setParams] = useSearchParams();
  const activeTab = TABS.some((tab) => tab.key === params.get('tab')) ? params.get('tab') : 'inbox';
  const accountFilter = params.get('mailbox') || '';
  const search = params.get('q') || '';
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  // Takes every change at once: two calls in one tick both read the same
  // pre-update params, so the second would drop the first one's change.
  const setParam = (changes) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true }
    );

  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const { unread: unreadThreads, overdue: overdueThreads, setUnread: setUnreadThreads, refreshUnread } = useUnread();
  const [accountErrors, setAccountErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [selected, setSelected] = useState(null);
  // account:uid -> body, so reopening a thread does not refetch what is already read.
  const [bodies, setBodies] = useState({});
  const [expanded, setExpanded] = useState([]);
  const [showQuoted, setShowQuoted] = useState([]);
  const [bodyLoading, setBodyLoading] = useState(false);
  const [bodyError, setBodyError] = useState(null);

  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const [replySent, setReplySent] = useState(null);
  const [replyFiles, setReplyFiles] = useState([]);
  const [replyCc, setReplyCc] = useState([]);
  const fileInputRef = useRef(null);
  const composeFileRef = useRef(null);
  // Null unless a new message is being written: one object so the form's
  // half-dozen fields do not become a half-dozen states.
  const [compose, setCompose] = useState(null);
  const [pipelineStatuses, setPipelineStatuses] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState(null);
  const latestRequest = useRef(0);

  const accountsById = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.id, a])),
    [accounts]
  );

  // A reply attaches to the newest message in the thread, which is what
  // keeps In-Reply-To pointing at the message being answered.
  const threadMailboxes = selected ? [...new Set(selected.messages.map((m) => m.account_id))] : [];
  // Everyone else who wrote in this conversation. Answering only the last of
  // them leaves the other two thinking nobody replied.
  const otherParticipants = (thread) => {
    const ours = new Set(accounts.map((account) => account.email.toLowerCase()));
    const answered = [...thread.messages].reverse().find((message) => !message.is_outbound);
    ours.add(answered ? answered.from_address.toLowerCase() : '');
    return [
      ...new Set(
        thread.messages
          .filter((message) => !message.is_outbound && !message.classification.startsWith('bounce'))
          .map((message) => message.from_address)
          .filter((address) => address && !ours.has(address.toLowerCase()))
      ),
    ];
  };

  // Theirs, not ours: replying to our own copy would address the mailbox itself.
  const replyTarget = selected
    ? [...selected.messages].reverse().find((message) => !message.is_outbound)
    : null;
  const replyBody = replyTarget ? bodies[messageKey(replyTarget)] : null;

  useEffect(() => {
    outreachApi
      .listAccounts()
      .then(setAccounts)
      .catch((e) => setLoadError(e?.response?.data?.detail || e.message));
    outreachApi
      .listPipelineStatuses()
      .then(setPipelineStatuses)
      .catch((e) => setLoadError(e?.response?.data?.detail || e.message));
  }, [token]);

  const suggestStatus = async () => {
    setAiBusy(true);
    setAiError(null);
    try {
      const suggestion = await outreachApi.labelReply({ replyText: replyBody.text, subject: selected.subject });
      window.alert(
        t('outreach.ai.suggestionResult', {
          status: suggestion.status,
          confidence: Math.round(suggestion.confidence * 100),
          reason: suggestion.reason,
        })
      );
    } catch (e) {
      setAiError(e?.response?.data?.detail || e.message);
    } finally {
      setAiBusy(false);
    }
  };

  // The whole conversation, ours included: a draft that only sees their last
  // line cannot tell a question from an out-of-office, or avoid repeating us.
  const conversationText = async (thread) => {
    const loaded = { ...bodies };
    for (const message of thread.messages) {
      const key = messageKey(message);
      if (!loaded[key]) {
        loaded[key] = await outreachApi.getMessageBody(message.account_id, message.uid, message.folder);
      }
    }
    setBodies(loaded);
    return thread.messages
      .map((message) => {
        const day = message.date ? new Date(message.date).toISOString().slice(0, 10) : '';
        const who = message.is_outbound ? 'Us' : 'Them';
        return `${who} (${message.from_address}, ${day}):\n${loaded[messageKey(message)].text}`;
      })
      .join('\n\n');
  };

  const draftAiReply = async () => {
    setAiBusy(true);
    setAiError(null);
    try {
      const draft = await outreachApi.draftReply({
        threadText: await conversationText(selected),
        senderName: accountsById[selected.account_id]?.display_name || null,
      });
      // Into the reply box, never onto the wire - a human presses send.
      writeReply(draft.body);
    } catch (e) {
      setAiError(e?.response?.data?.detail || e.message);
    } finally {
      setAiBusy(false);
    }
  };

  // Resets to the first page: any change to what's being viewed (tab,
  // mailbox, search) invalidates the scrolled-in pages from before it.
  const loadInbox = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // Responses can land out of order, and a slow first request finishing
    // after a fast second one would repaint the tab the user already left.
    const request = (latestRequest.current += 1);
    try {
      const data = await outreachApi.listInbox({
        ...tabFilters(activeTab),
        q: debouncedSearch || undefined,
        accountId: accountFilter || undefined,
        limit: PAGE_SIZE,
        offset: 0,
      });
      if (request !== latestRequest.current) return;
      setThreads(data.threads);
      setTotal(data.total);
      setUnreadThreads(data.unread_threads);
      setAccountErrors(data.errors || {});
    } catch (e) {
      if (request !== latestRequest.current) return;
      setLoadError(e?.response?.data?.detail || e.message);
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, [activeTab, accountFilter, debouncedSearch, setUnreadThreads]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || threads.length >= total) return;
    setLoadingMore(true);
    const request = latestRequest.current;
    try {
      const data = await outreachApi.listInbox({
        ...tabFilters(activeTab),
        q: debouncedSearch || undefined,
        accountId: accountFilter || undefined,
        limit: PAGE_SIZE,
        offset: threads.length,
      });
      if (request !== latestRequest.current) return;
      setThreads((current) => [...current, ...data.threads]);
      setTotal(data.total);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e.message);
    } finally {
      setLoadingMore(false);
    }
  }, [activeTab, accountFilter, debouncedSearch, loading, loadingMore, threads.length, total]);

  const handleListScroll = (event) => {
    const el = event.currentTarget;
    // Fires a little before the physical bottom so the next page is ready
    // before the user's scroll outruns it.
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) loadMore();
  };

  // Answers whether the message is on screen: one that is not must not be marked read.
  const loadBody = async (message) => {
    if (bodies[messageKey(message)]) return true;
    setBodyLoading(true);
    setBodyError(null);
    try {
      const loaded = await outreachApi.getMessageBody(message.account_id, message.uid, message.folder);
      setBodies((current) => ({ ...current, [messageKey(message)]: loaded }));
      return true;
    } catch (e) {
      // 404 means the mail itself is gone, not that we broke: say so plainly
      // and reload the list, which the server has just un-cached.
      if (e?.response?.status === 404) {
        setBodyError(t('outreach.messageGone'));
        setSelected(null);
        setParam({ thread: '' });
        await loadInbox();
        return false;
      }
      setBodyError(e?.response?.data?.detail || e.message);
      return false;
    } finally {
      setBodyLoading(false);
    }
  };

  const openThread = async (thread) => {
    setCompose(null);
    setSelected(thread);
    setParam({ thread: thread.id });
    setBodyError(null);
    setReplyText(loadReplyDraft(thread.id));
    setReplyError(null);
    setReplySent(null);
    setReplyFiles([]);
    setReplyCc(otherParticipants(thread));
    // Only the newest message opens by itself: a fifty-message thread must
    // not fetch fifty bodies to show one answer.
    const newest = thread.messages[thread.messages.length - 1];
    setExpanded([messageKey(newest)]);
    setShowQuoted([]);
    if (!(await loadBody(newest))) return;
    // The reply box and the AI actions read their last message, which is not
    // the one on screen when our own answer came after it.
    const target = [...thread.messages].reverse().find((message) => !message.is_outbound);
    if (target && messageKey(target) !== messageKey(newest)) await loadBody(target);

    if (thread.unread_count > 0) {
      try {
        // A thread can merge messages from several mailboxes, and an IMAP uid
        // only means something inside its own - sending them all to one
        // mailbox left the real ones unread and flagged strangers instead.
        await Promise.all(
          Object.entries(byAccount(thread.messages.filter((m) => m.is_unread))).map(
            ([accountId, uids]) => outreachApi.markMessagesRead(accountId, uids)
          )
        );
        setUnreadThreads((count) => count - 1);
        // The row stays on the unread tab until the next refresh: pulling it
        // out from under the pointer reads as mail going missing.
        setThreads((current) =>
          current.map((t) =>
            t.id === thread.id
              ? { ...t, unread_count: 0, messages: t.messages.map((m) => ({ ...m, is_unread: false })) }
              : t
          )
        );
      } catch (e) {
        // Non-fatal: the message is already open and readable, it just
        // stays listed as unread until the next refresh.
        setLoadError(e?.response?.data?.detail || e.message);
      }
    }
  };

  const setHandled = async (thread, handled) => {
    setLoadError(null);
    try {
      const updated = await outreachApi.setThreadHandled(thread.id, handled, accountFilter || undefined);
      setSelected((current) => (current && current.id === thread.id ? { ...current, ...updated } : current));
      setThreads((current) =>
        activeTab === 'waiting' && handled
          ? current.filter((t) => t.id !== thread.id)
          : current.map((t) => (t.id === thread.id ? { ...t, ...updated } : t))
      );
      refreshUnread();
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e.message);
    }
  };

  const markUnread = async (thread) => {
    try {
      await Promise.all(
        Object.entries(byAccount(thread.messages)).map(([accountId, uids]) =>
          outreachApi.markMessagesRead(accountId, uids, false)
        )
      );
      setUnreadThreads((count) => count + 1);
      setThreads((current) =>
        current.map((t) =>
          t.id === thread.id
            ? {
                ...t,
                unread_count: t.message_count,
                messages: t.messages.map((m) => ({ ...m, is_unread: true })),
              }
            : t
        )
      );
      // Back to the list, or the open thread would be marked read again by
      // the next reload of the very link that is still in the address bar.
      setSelected(null);
      setParam({ thread: '' });
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e.message);
    }
  };

  const toggleStar = async (event, thread) => {
    event.stopPropagation();
    const nextStarred = thread.starred_count === 0;
    try {
      await Promise.all(
        Object.entries(byAccount(thread.messages)).map(([accountId, uids]) =>
          outreachApi.setMessagesStarred(accountId, uids, nextStarred)
        )
      );
      setThreads((current) =>
        current
          .map((t) =>
            t.id === thread.id
              ? {
                  ...t,
                  starred_count: nextStarred ? t.messages.length : 0,
                  messages: t.messages.map((m) => ({ ...m, is_starred: nextStarred })),
                }
              : t
          )
          .filter((t) => activeTab !== 'starred' || t.starred_count > 0)
      );
      if (activeTab === 'starred' && !nextStarred) setTotal((current) => current - 1);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e.message);
    }
  };

  const toggleMessage = async (message) => {
    const key = messageKey(message);
    setExpanded((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));
    await loadBody(message);
  };

  const toggleQuoted = (key) =>
    setShowQuoted((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    );

  const attachedBytes = replyFiles.reduce((total, file) => total + file.size, 0);

  // Also refused server-side; this only saves uploading it first.
  const attachFiles = (chosen) => {
    const next = [...replyFiles, ...chosen];
    const total = next.reduce((sum, file) => sum + file.size, 0);
    if (total > MAX_REPLY_ATTACHMENT_BYTES) {
      setReplyError(t('outreach.attachTooLarge', { limit: MAX_REPLY_ATTACHMENT_MB }));
      return;
    }
    setReplyError(null);
    setReplyFiles(next);
  };

  const writeReply = (text) => {
    setReplyText(text);
    saveReplyDraft(selected.id, text);
  };

  const sendReply = async () => {
    if (!selected || (!replyText.trim() && !replyFiles.length)) return;
    setSending(true);
    setReplyError(null);
    try {
      const result = await outreachApi.replyToMessage(replyTarget.account_id, {
        messageId: replyTarget.message_id,
        folder: replyTarget.folder,
        body: replyText,
        files: replyFiles,
        cc: replyCc,
      });
      // Shown from what we just sent, not from a refetch: the copy in Sent
      // takes an IMAP round trip to appear, and the answer is on screen now.
      const sent = {
        account_id: replyTarget.account_id,
        uid: `pending-${result.message_id}`,
        folder: 'Sent',
        is_outbound: true,
        message_id: result.message_id,
        in_reply_to: replyTarget.message_id,
        references: [],
        from_address: accountsById[selected.account_id]?.email || '',
        from_name: null,
        to_address: replyTarget.from_address,
        cc_addresses: replyCc,
        subject: selected.subject,
        date: result.sent_at,
        has_attachments: replyFiles.length > 0,
        is_unread: false,
        is_starred: false,
        classification: 'human_reply',
        preview: replyText.slice(0, 140),
      };
      const key = messageKey(sent);
      setBodies((current) => ({
        ...current,
        [key]: { text: replyText, quoted: '', was_html_only: false, attachments: [] },
      }));
      setExpanded((current) => [...current, key]);
      setSelected((current) => ({
        ...current,
        messages: [...current.messages, sent],
        message_count: current.message_count + 1,
      }));
      setThreads((current) =>
        current.map((thread) =>
          thread.id === selected.id
            ? {
                ...thread,
                messages: [...thread.messages, sent],
                message_count: thread.message_count + 1,
                last_at: sent.date,
                preview: sent.preview,
              }
            : thread
        )
      );
      setReplySent(result);
      setReplyText('');
      clearReplyDraft(selected.id);
      setReplyFiles([]);
      refreshUnread();
    } catch (e) {
      setReplyError(e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  const downloadAttachment = async (message, attachment) => {
    const blob = await outreachApi.downloadAttachment(
      message.account_id,
      message.uid,
      attachment.index,
      message.folder
    );
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const startCompose = () => {
    setSelected(null);
    setParam({ thread: '' });
    setCompose({
      accountId: accountFilter || accounts[0]?.id || '',
      to: '',
      cc: '',
      subject: '',
      body: '',
      files: [],
      sending: false,
      error: null,
      sent: null,
    });
  };

  const sendComposed = async () => {
    setCompose((current) => ({ ...current, sending: true, error: null }));
    try {
      const result = await outreachApi.sendMessage(compose.accountId, {
        to: compose.to.trim(),
        subject: compose.subject,
        body: compose.body,
        cc: compose.cc.split(',').map((address) => address.trim()).filter(Boolean),
        files: compose.files,
      });
      setCompose((current) => ({
        ...current,
        sending: false,
        sent: result,
        to: '',
        cc: '',
        subject: '',
        body: '',
        files: [],
      }));
    } catch (e) {
      setCompose((current) => ({
        ...current,
        sending: false,
        error: e?.response?.data?.detail || e.message,
      }));
    }
  };

  const switchTab = (key) => {
    setParam({ tab: key === 'inbox' ? '' : key, thread: '' });
    setSelected(null);
  };

  // A thread id in the URL means a link to one conversation opens it, and a
  // reload keeps it open instead of dropping back to the empty pane.
  const wantedThread = params.get('thread');
  useEffect(() => {
    if (!wantedThread || selected?.id === wantedThread) return;
    const match = threads.find((thread) => thread.id === wantedThread);
    if (match) {
      openThread(match);
      return;
    }
    // Reading it marks it read, so the tab it was opened from - unread, say -
    // no longer lists it: the link has to be answerable without the list.
    if (loading) return;
    outreachApi
      .getThread(wantedThread, accountFilter || undefined)
      .then(openThread)
      .catch((e) => {
        if (e?.response?.status === 404) setBodyError(t('outreach.threadGone'));
        else setLoadError(e?.response?.data?.detail || e.message);
        setParam({ thread: '' });
      });
  }, [wantedThread, threads, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // j/k/Enter/s, the keys every mail client already trains people on. Ignored
  // while a field has focus, or "s" would land in the reply box.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || event.target.isContentEditable) return;
      if (!threads.length) return;

      const current = selected ? threads.findIndex((thread) => thread.id === selected.id) : -1;
      if (event.key === 'j' || event.key === 'k') {
        event.preventDefault();
        const step = event.key === 'j' ? 1 : -1;
        const next = Math.min(Math.max(current + step, 0), threads.length - 1);
        openThread(threads[next]);
        document.querySelector('.unibox-item.is-selected')?.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 's' && selected) {
        event.preventDefault();
        toggleStar({ stopPropagation: () => {} }, selected);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const brokenAccounts = Object.entries(accountErrors);
  const mailboxColor = (accountId) => hueFor(accounts, accountId);
  const tabCounts = { unread: unreadThreads, overdue: overdueThreads };

  return (
    <div className="unibox">
      <div className="toolbar">
        <h1 className="toolbar-title">{t('nav.outreach', 'Conversations')}</h1>
        <div className="toolbar-filters">
          <div className="unibox-tabs">
            {TABS.map((tab) => {
              const count = tab.count ? tabCounts[tab.count] : 0;
              return (
                <button
                  key={tab.key}
                  className={[
                    'unibox-tab',
                    activeTab === tab.key ? 'is-active' : '',
                    count > 0 ? 'has-count' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => switchTab(tab.key)}
                >
                  {t(tab.labelKey)}
                  {count > 0 && (
                    <span
                      className={`unibox-tab-count${tab.count === 'overdue' ? ' is-overdue' : ''}`}
                      data-testid={`tab-count-${tab.key}`}
                      title={t(tab.countTitleKey, { count })}
                    >
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <input
            className="unibox-select unibox-search"
            type="search"
            placeholder={t('outreach.searchPlaceholder')}
            value={search}
            onChange={(e) => setParam({ q: e.target.value })}
          />
          {accounts.length > 1 && (
            <select
              className="unibox-select"
              value={accountFilter}
              onChange={(e) => setParam({ mailbox: e.target.value })}
            >
              <option value="">{t('outreach.allMailboxes')}</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.email}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="toolbar-actions">
          <button className="unibox-refresh" onClick={startCompose} disabled={!accounts.length}>
            <PenSquare size={14} /> {t('outreach.compose.new')}
          </button>
          <Link className="unibox-refresh" to="/outreach/mailboxes">
            <Inbox size={14} /> {t('outreach.mailboxes')}
          </Link>
          <button className="unibox-refresh" onClick={loadInbox} disabled={loading}>
            <RefreshCw size={14} /> {t('outreach.refresh')}
          </button>
        </div>
      </div>

      {brokenAccounts.map(([accountId, message]) => (
        <div className="unibox-banner" key={accountId}>
          <div className="unibox-banner-row">
            <span>
              <AlertCircle size={14} />{' '}
              {t('outreach.mailboxBroken', { email: accountsById[accountId]?.email || accountId })}: {message}
            </span>
          </div>
        </div>
      ))}

      {loadError && <div className="unibox-banner">{String(loadError)}</div>}

      <div className={`unibox-body${selected ? ' has-selection' : ''}`}>
        <div className="unibox-list" onScroll={handleListScroll}>
          {loading && <div className="unibox-empty">{t('outreach.loading')}</div>}

          {!loading && threads.length === 0 && accounts.length === 0 && (
            <EmptyState
              icon={<Inbox size={28} />}
              title={t('outreach.emptyNoMailbox')}
              text={t('outreach.emptyNoMailboxLink')}
            >
              <Link className="unibox-button" to="/outreach/mailboxes">
                {t('outreach.connectMailbox')}
              </Link>
            </EmptyState>
          )}

          {!loading && threads.length === 0 && accounts.length > 0 && (
            <EmptyState
              icon={<Inbox size={28} />}
              title={t('outreach.emptyTitle')}
              text={
                debouncedSearch
                  ? t('outreach.noResults')
                  : activeTab === 'campaignReplies'
                  ? t('outreach.emptyCampaignReplies')
                  : t('outreach.emptyHint')
              }
            >
              {debouncedSearch && (
                <button className="unibox-refresh" onClick={() => setParam({ q: '' })}>
                  {t('outreach.clearSearch')}
                </button>
              )}
            </EmptyState>
          )}

          {!loading &&
            threads.map((thread) => (
              <div
                key={thread.id}
                className={[
                  'unibox-item',
                  thread.unread_count ? 'is-unread' : '',
                  selected && selected.id === thread.id ? 'is-selected' : '',
                ].join(' ')}
                onClick={() => openThread(thread)}
              >
                <div className="unibox-item-top">
                  <span className="unibox-item-from">
                    {accounts.length > 1 &&
                      [...new Set(thread.messages.map((m) => m.account_id))].map((accountId) => (
                        <span
                          key={accountId}
                          className="unibox-mailbox-dot"
                          style={{ background: mailboxColor(accountId) }}
                          title={accountsById[accountId]?.email || accountId}
                        />
                      ))}
                    {thread.counterparties.join(', ')}
                  </span>
                  <span className="unibox-item-date">
                    <button
                      className={`unibox-star${thread.starred_count ? ' is-starred' : ''}`}
                      onClick={(e) => toggleStar(e, thread)}
                      title={thread.starred_count ? t('outreach.unstarThread') : t('outreach.starThread')}
                    >
                      <Star size={14} fill={thread.starred_count ? 'currentColor' : 'none'} />
                    </button>
                    {thread.message_count > 1 && (
                      <span className="unibox-thread-count">{thread.message_count}</span>
                    )}
                    {thread.reply_overdue && (
                      <span className="unibox-overdue" title={t('outreach.waitingForUs')}>
                        {t('outreach.waitingDays', { count: waitingDays(thread.waiting_since) })}
                      </span>
                    )}
                    {formatDate(thread.last_at, i18n.language)}
                  </span>
                </div>
                <div className="unibox-item-subject">
                  {thread.has_attachments && <Paperclip size={12} />} {thread.subject || t('outreach.noSubject')}
                </div>
                {thread.preview && <div className="unibox-item-preview">{thread.preview}</div>}
                {thread.classifications.some((kind) => kind !== 'human_reply') && (
                  <div className="unibox-badges">
                    {thread.classifications
                      .filter((kind) => kind !== 'human_reply')
                      .map((kind) => (
                        <span key={kind} className={`unibox-badge ${BADGE_CLASS[kind] || ''}`}>
                          {t(`outreach.classification.${kind}`, kind)}
                        </span>
                      ))}
                  </div>
                )}
              </div>
            ))}

          {!loading && loadingMore && <div className="unibox-load-more">{t('outreach.loading')}</div>}
        </div>

        <div className="unibox-detail">
          {compose && (
            <div className="unibox-compose">
              <div className="unibox-reply-recipients">
                <select
                  className="unibox-select"
                  value={compose.accountId}
                  onChange={(e) => setCompose((c) => ({ ...c, accountId: e.target.value }))}
                >
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.email}
                    </option>
                  ))}
                </select>
                <button className="unibox-page-button" onClick={() => setCompose(null)}>
                  {t('outreach.compose.cancel')}
                </button>
              </div>
              <input
                className="unibox-compose-field"
                placeholder={t('outreach.compose.to')}
                value={compose.to}
                onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))}
              />
              <input
                className="unibox-compose-field"
                placeholder={t('outreach.compose.cc')}
                value={compose.cc}
                onChange={(e) => setCompose((c) => ({ ...c, cc: e.target.value }))}
              />
              <input
                className="unibox-compose-field"
                placeholder={t('outreach.compose.subject')}
                value={compose.subject}
                onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
              />
              <textarea
                className="unibox-reply-textarea"
                placeholder={t('outreach.compose.body')}
                value={compose.body}
                onChange={(e) => setCompose((c) => ({ ...c, body: e.target.value }))}
              />
              {compose.files.length > 0 && (
                <div className="unibox-reply-files">
                  {compose.files.map((file, index) => (
                    <span className="unibox-reply-file" key={`${file.name}-${index}`}>
                      <Paperclip size={13} /> {file.name} ({formatSize(file.size)})
                      <button
                        type="button"
                        className="unibox-reply-file-remove"
                        aria-label={t('outreach.attachRemove', { name: file.name })}
                        onClick={() =>
                          setCompose((c) => ({ ...c, files: c.files.filter((_, i) => i !== index) }))
                        }
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="unibox-reply-actions">
                <input
                  type="file"
                  multiple
                  className="unibox-reply-file-input"
                  data-testid="compose-attach-input"
                  ref={composeFileRef}
                  onChange={(e) => {
                    const picked = Array.from(e.target.files);
                    setCompose((c) => ({ ...c, files: [...c.files, ...picked] }));
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  className="unibox-page-button"
                  onClick={() => composeFileRef.current.click()}
                  disabled={compose.sending}
                >
                  <Paperclip size={13} /> {t('outreach.attach')}
                </button>
                <button
                  className="unibox-button"
                  onClick={sendComposed}
                  disabled={compose.sending || !compose.to.trim() || !compose.subject.trim()}
                >
                  {compose.sending ? t('outreach.sending') : t('outreach.send')}
                </button>
                {compose.sent && <span className="unibox-reply-hint">{t('outreach.replySent')}</span>}
                {compose.error && <span className="unibox-error">{String(compose.error)}</span>}
              </div>
            </div>
          )}

          {!selected && !compose && (
            <EmptyState text={bodyError ? String(bodyError) : t('outreach.selectMessage')}>
              {!bodyError && <span className="unibox-reply-hint">{t('outreach.keyboardHint')}</span>}
            </EmptyState>
          )}

          {selected && !compose && (
            <>
              <button
                className="unibox-back"
                onClick={() => {
                  setSelected(null);
                  setParam({ thread: '' });
                }}
              >
                <ChevronLeft size={14} /> {t('outreach.previous')}
              </button>
              <h2 className="unibox-detail-subject">{selected.subject || t('outreach.noSubject')}</h2>
              <div className="unibox-detail-meta">
                <span>{selected.counterparties.join(', ')}</span>
                <span>→ {accountsById[selected.account_id]?.email || ''}</span>
                <span
                  className="unibox-icon-note"
                  title={t('outreach.messagesInThread', { count: selected.message_count })}
                >
                  <Mail size={14} /> {selected.message_count}
                </span>
                <button
                  className="unibox-icon-button"
                  onClick={() => markUnread(selected)}
                  title={t('outreach.markUnread')}
                  aria-label={t('outreach.markUnread')}
                >
                  <MailOpen size={14} />
                </button>
                <button
                  className={`unibox-icon-button${selected.handled ? ' is-handled' : ''}`}
                  data-testid="thread-handled"
                  onClick={() => setHandled(selected, !selected.handled)}
                  title={t(selected.handled ? 'outreach.needsAnswerAgain' : 'outreach.noAnswerNeeded')}
                  aria-label={t(selected.handled ? 'outreach.needsAnswerAgain' : 'outreach.noAnswerNeeded')}
                >
                  <Check size={14} />
                </button>
              </div>
              <div className="unibox-detail-meta">
                {pipelineStatuses.length > 0 && (
                  <UniboxLeadStatus
                    address={selected.counterparties[0]}
                    messages={selected.messages}
                    statuses={pipelineStatuses}
                  />
                )}
                <button
                  className="unibox-icon-button"
                  disabled={aiBusy || !replyBody}
                  onClick={suggestStatus}
                  title={t('outreach.ai.suggestStatus')}
                  aria-label={t('outreach.ai.suggestStatus')}
                >
                  <Tag size={14} />
                </button>
                <button
                  className="unibox-icon-button"
                  disabled={aiBusy || !replyBody}
                  onClick={draftAiReply}
                  title={t('outreach.ai.draft')}
                  aria-label={t('outreach.ai.draft')}
                >
                  <Sparkles size={14} />
                </button>
                {aiBusy && <span className="lead-history-source">{t('outreach.ai.working')}</span>}
                {aiError && <span className="unibox-error">{String(aiError)}</span>}
              </div>

              {bodyError && <div className="unibox-error">{String(bodyError)}</div>}

              {selected.messages.map((message) => {
                const key = messageKey(message);
                const open = expanded.includes(key);
                const loaded = bodies[key];
                return (
                  <div
                    key={key}
                    className={`unibox-message${open ? ' is-open' : ''}${
                      message.is_outbound ? ' is-outbound' : ''
                    }`}
                  >
                    <button className="unibox-message-head" onClick={() => toggleMessage(message)}>
                      <span className="unibox-item-from">{message.from_name || message.from_address}</span>
                      <span className="unibox-item-date">
                        {/* Threads merge across mailboxes, so which one took
                            this message is not implied by the thread. */}
                        {threadMailboxes.length > 1 && (
                          <span className="unibox-badge is-mailbox">
                            <span
                              className="unibox-mailbox-dot"
                              style={{ background: mailboxColor(message.account_id) }}
                            />
                            {accountsById[message.account_id]?.email || message.account_id}
                          </span>
                        )}
                        {message.date ? new Date(message.date).toLocaleString() : ''}
                      </span>
                    </button>

                    <div className="unibox-message-to">
                      {t('outreach.sentTo', {
                        addresses: [message.to_address, ...message.cc_addresses]
                          .filter(Boolean)
                          .join(', '),
                      })}
                    </div>

                    {open && !loaded && bodyLoading && (
                      <div className="unibox-empty">{t('outreach.loading')}</div>
                    )}

                    {open && loaded && (
                      <>
                        <div className="unibox-detail-text">{loaded.text}</div>
                        {loaded.quoted && (
                          <>
                            <button className="unibox-page-button" onClick={() => toggleQuoted(key)}>
                              {showQuoted.includes(key)
                                ? t('outreach.hideQuoted')
                                : t('outreach.showQuoted')}
                            </button>
                            {showQuoted.includes(key) && (
                              <div className="unibox-detail-text is-quoted">{loaded.quoted}</div>
                            )}
                          </>
                        )}
                        {loaded.attachments.length > 0 && (
                          <div className="unibox-attachments">
                            {loaded.attachments.map((attachment) => (
                              <button
                                key={attachment.index}
                                className="unibox-attachment"
                                onClick={() => downloadAttachment(message, attachment)}
                              >
                                <Paperclip size={14} /> {attachment.filename} (
                                {formatSize(attachment.size_bytes)})
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              <div className="unibox-reply">
                {replyTarget && (
                  <div className="unibox-reply-recipients">
                    <span className="unibox-reply-hint">
                      {t('outreach.replyTo', { email: replyTarget.from_address })}
                    </span>
                    {replyCc.length > 0 && (
                      <span className="unibox-reply-hint">{t('outreach.replyCc')}</span>
                    )}
                    {replyCc.map((address) => (
                      <span className="unibox-reply-file" key={address}>
                        {address}
                        <button
                          type="button"
                          className="unibox-reply-file-remove"
                          aria-label={t('outreach.ccRemove', { email: address })}
                          onClick={() => setReplyCc(replyCc.filter((entry) => entry !== address))}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <textarea
                  className="unibox-reply-textarea"
                  value={replyText}
                  placeholder={t('outreach.replyPlaceholder')}
                  onChange={(e) => writeReply(e.target.value)}
                />
                {replyFiles.length > 0 && (
                  <div className="unibox-reply-files">
                    {replyFiles.map((file, index) => (
                      <span className="unibox-reply-file" key={`${file.name}-${index}`}>
                        <Paperclip size={13} /> {file.name} ({formatSize(file.size)})
                        <button
                          type="button"
                          className="unibox-reply-file-remove"
                          aria-label={t('outreach.attachRemove', { name: file.name })}
                          onClick={() => setReplyFiles(replyFiles.filter((_, i) => i !== index))}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                    <span className="unibox-reply-hint">
                      {t('outreach.attachTotal', {
                        used: formatSize(attachedBytes),
                        limit: MAX_REPLY_ATTACHMENT_MB,
                      })}
                    </span>
                  </div>
                )}
                <div className="unibox-reply-actions">
                  <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    className="unibox-reply-file-input"
                    data-testid="reply-attach-input"
                    onChange={(e) => {
                      attachFiles(Array.from(e.target.files));
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="unibox-page-button"
                    onClick={() => fileInputRef.current.click()}
                    disabled={sending}
                  >
                    <Paperclip size={13} /> {t('outreach.attach')}
                  </button>
                  <button
                    className="unibox-button"
                    onClick={sendReply}
                    disabled={sending || (!replyText.trim() && !replyFiles.length)}
                  >
                    {sending ? t('outreach.sending') : t('outreach.send')}
                  </button>
                  <span className="unibox-reply-hint">
                    {t('outreach.replyFrom', {
                      email: accountsById[selected.account_id]?.email || selected.account_id,
                    })}
                  </span>
                  {replySent && <span className="unibox-reply-hint">{t('outreach.replySent')}</span>}
                  {replyError && <span className="unibox-error">{String(replyError)}</span>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
